import { useMemo, useState } from 'react';
import { PcsoGame, PcsoGameId, PcsoResponse } from './pcso';

function formatDateTime(value: string | null, includeTime = true): string {
  if (!value) return 'Unavailable';
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return 'Unavailable';
  return new Intl.DateTimeFormat('en-PH', {
    timeZone: 'Asia/Manila',
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    ...(includeTime ? { hour: 'numeric', minute: '2-digit', hour12: true } : {}),
  }).format(date);
}

function GameCard(props: {
  game: PcsoGame;
  selected: boolean;
  threshold: number;
  onSelect: () => void;
  onGoToDate: (date: string) => void;
}) {
  const { game } = props;
  const highlighted = game.jackpotStatus === 'official' && game.jackpot !== null && game.jackpot >= props.threshold;
  return (
    <article className={'pcso-game-card ' + (props.selected ? 'selected ' : '') + (highlighted ? 'jackpot-highlight' : '')}>
      <button type="button" className="pcso-game-summary" onClick={props.onSelect} aria-expanded={props.selected}>
        <span className="pcso-game-code">{game.id}</span>
        <span className="pcso-game-copy"><strong>{game.name}</strong><small>Next draw · {formatDateTime(game.nextDraw)}</small></span>
        <span className="pcso-jackpot"><small>Current jackpot</small><strong>{game.jackpotFormatted || 'Unavailable'}</strong><em className={'pcso-status ' + game.jackpotStatus}>{game.jackpotStatus === 'official' ? 'Official' : game.jackpotStatus === 'estimated' ? 'Estimated' : 'Jackpot information unavailable'}</em></span>
        <span className="pcso-card-caret" aria-hidden="true">{props.selected ? '−' : '+'}</span>
      </button>
      {props.selected && <div className="pcso-game-details">
        <div className="pcso-detail-block">
          <span className="eyebrow">Previous result</span>
          <strong>{game.lastDraw.date ? formatDateTime(game.lastDraw.drawTime || game.lastDraw.date, false) : 'No verified result available'}</strong>
          {game.lastDraw.winningNumbers.length > 0
            ? <div className="pcso-balls" aria-label={'Winning numbers ' + game.lastDraw.winningNumbers.join(', ')}>{game.lastDraw.winningNumbers.map((number, index) => <span key={number + '-' + index}>{number.padStart(2, '0')}</span>)}</div>
            : <p className="pcso-empty">Winning numbers unavailable.</p>}
        </div>
        <dl className="pcso-facts">
          <div><dt>Previous jackpot</dt><dd>{game.lastDraw.jackpotFormatted || 'Unavailable for this result'}</dd></div>
          <div><dt>Jackpot winners</dt><dd>{game.lastDraw.jackpotWinners === null ? 'Unavailable' : game.lastDraw.jackpotWinners.toLocaleString('en-PH')}</dd></div>
          <div><dt>Draw time</dt><dd>{game.drawTime} · Asia/Manila</dd></div>
        </dl>
        <div className="pcso-detail-actions">
          {game.nextDraw && <button type="button" className="secondary-button" onClick={() => props.onGoToDate(game.nextDraw!.slice(0, 10))}>View draw date</button>}
          <a className="secondary-button" href={game.sourceUrl} target="_blank" rel="noreferrer">Open PCSO source ↗</a>
        </div>
      </div>}
    </article>
  );
}

export default function PCSOTracker(props: {
  data: PcsoResponse | null;
  loading: boolean;
  error: string;
  selectedGameId: PcsoGameId | null;
  showEvents: boolean;
  threshold: number;
  onSelectGame: (id: PcsoGameId) => void;
  onRefresh: () => void;
  onGoToDate: (date: string) => void;
  onToggleEvents: (value: boolean) => void;
  onThreshold: (value: number) => void;
}) {
  const [expanded, setExpanded] = useState(true);
  const selectedId = props.selectedGameId || props.data?.games[0]?.id || null;
  const updated = useMemo(() => formatDateTime(props.data?.lastUpdated || null), [props.data?.lastUpdated]);
  return (
    <section className="pcso-view">
      <div className="pcso-hero">
        <div><span className="eyebrow">Philippine Charity Sweepstakes Office</span><h2>PCSO Lotto Tracker</h2><p>Official advertised jackpots, upcoming 9 PM draws, and the latest verified winning numbers in Philippine time.</p></div>
        <div className="pcso-hero-actions"><button type="button" className="secondary-button" onClick={() => setExpanded((value) => !value)}>{expanded ? 'Collapse tracker' : 'Expand tracker'}</button><button type="button" className="primary-button" disabled={props.loading} onClick={props.onRefresh}>{props.loading ? 'Refreshing…' : 'Refresh'}</button></div>
      </div>
      <div className="pcso-toolbar">
        <label className="pcso-toggle"><input type="checkbox" checked={props.showEvents} onChange={(event) => props.onToggleEvents(event.target.checked)} /><span>Show PCSO Lotto events in Calendar</span></label>
        <label className="pcso-threshold"><span>Highlight official jackpots from</span><span className="pcso-threshold-input">₱<input type="number" min="1000000" step="1000000" value={props.threshold} onChange={(event) => props.onThreshold(Math.max(1000000, Number(event.target.value) || 100000000))} /></span></label>
      </div>
      <div className="pcso-verification" role="status"><span className={'pcso-live-dot ' + (props.data?.stale ? 'stale' : '')} />{props.data?.lastUpdated ? (props.data.stale ? 'Last successful verification ' : 'Verified ') + updated : props.loading ? 'Checking PCSO sources…' : 'No successful verification yet'}</div>
      {props.error && <div className="pcso-message error">{props.error}</div>}
      {props.data?.warning && <div className="pcso-message stale">{props.data.warning}</div>}
      {expanded && <div className="pcso-games" aria-busy={props.loading}>
        {!props.data && props.loading && Array.from({ length: 5 }, (_, index) => <div className="pcso-game-card loading" key={index}><span /></div>)}
        {props.data?.games.map((game) => <GameCard key={game.id} game={game} selected={selectedId === game.id} threshold={props.threshold} onSelect={() => props.onSelectGame(game.id)} onGoToDate={props.onGoToDate} />)}
        {props.data && props.data.games.length === 0 && <div className="pcso-message">No PCSO games are available.</div>}
      </div>}
      <footer className="pcso-footer"><p>Jackpot labels refer to the advertised upcoming prize. A previous draw jackpot is shown separately and only when it matches that result’s date.</p>{props.data && <div><a href={props.data.sourceUrl} target="_blank" rel="noreferrer">PCSO LottoMatik data source ↗</a><a href={props.data.scheduleSourceUrl} target="_blank" rel="noreferrer">Official draw schedule ↗</a></div>}</footer>
    </section>
  );
}
